import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Edit2, Trash2, Search, UserPlus, ShieldCheck, Mail, Key, User, X } from 'lucide-react';
import axios from 'axios';
import { API_BASE_LOCAL_URL } from '../../../config';

const UserManagement = forwardRef(({ currentUser }, ref) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'PRODUCT_MANAGER',
  });
  const [editUserId, setEditUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showModal, setShowModal] = useState(false);

  const token = localStorage.getItem('token');
  const API_URL = `${API_BASE_LOCAL_URL}/api/users`;

  const fetchUsers = async () => {
    if (currentUser.role !== 'ADMIN') return;
    setIsLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editUserId ? 'PUT' : 'POST';
    const url = editUserId ? `${API_URL}/${editUserId}` : API_URL;

    try {
      const res = await axios({
        method,
        url,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: formData
      });

      if (res.status === 200 || res.status === 201) {
        fetchUsers();
        closeAndResetModal();
      }
    } catch (err) {
      alert(err.response?.data || 'Error saving user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
    try {
      const res = await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      email: user.email,
      password: '', // Keep empty for security
      role: user.role || 'PRODUCT_MANAGER',
    });
    setEditUserId(user.id);
    setShowModal(true);
  };

  const closeAndResetModal = () => {
    setFormData({ username: '', email: '', password: '', role: 'PRODUCT_MANAGER' });
    setEditUserId(null);
    setShowModal(false);
  };

  useImperativeHandle(ref, () => ({
    openUserModal: () => setShowModal(true),
    closeUserModal: closeAndResetModal,
  }));

  const filteredUsers = Array.isArray(users) ? users.filter((user) => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          user.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  }) : [];

  const getRoleBadge = (role) => {
    const styles = {
      ADMIN: "bg-danger text-danger",
      PRODUCT_MANAGER: "bg-primary text-primary",
      DATA_CURATOR: "bg-info text-info"
    };
    return styles[role] || "bg-secondary text-secondary";
  };

  if (currentUser.role !== 'ADMIN') {
    return (
      <div className="card border-0 shadow-sm p-5 text-center">
        <ShieldCheck size={48} className="text-warning mx-auto mb-3" />
        <h4 className="fw-bold">Access Restricted</h4>
        <p className="text-muted">Only administrators can manage system users.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header Section */}
      <div className="row align-items-center mb-4 g-3">
        <div className="col-md-4">
          <div className="position-relative">
            <Search size={18} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
            <input
              type="text"
              className="form-control border-0 shadow-sm ps-5 py-2"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select 
            value={filterRole} 
            onChange={(e) => setFilterRole(e.target.value)} 
            className="form-select border-0 shadow-sm py-2"
          >
            <option value="all">All Roles</option>
            <option value="PRODUCT_MANAGER">Product Managers</option>
            <option value="DATA_CURATOR">Data Curators</option>
            <option value="ADMIN">Administrators</option>
          </select>
        </div>
        <div className="col-md-5 text-md-end">
          <button onClick={() => setShowModal(true)} className="btn btn-success shadow-sm px-4 py-2 d-inline-flex align-items-center gap-2 rounded-3">
            <UserPlus size={18} />
            <span>Add New Staff</span>
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="px-4 py-3 text-muted small text-uppercase fw-bold">User Information</th>
                <th className="py-3 text-muted small text-uppercase fw-bold">Access Level</th>
                <th className="py-3 text-muted small text-uppercase fw-bold text-end px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="3" className="text-center py-5 text-muted">Loading user data...</td></tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-4 py-3">
                      <div className="d-flex align-items-center">
                        <div className="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold shadow-sm" style={{ width: '42px', height: '42px' }}>
                          {user.username?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-bold text-dark">{user.username}</div>
                          <div className="text-muted small">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge rounded-pill px-3 py-2 fw-medium ${getRoleBadge(user.role)} bg-opacity-10`}>
                        {user.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="text-end px-4">
                      <div className="d-flex justify-content-end gap-2">
                        <button onClick={() => handleEdit(user)} className="btn btn-light btn-sm rounded-circle p-2 text-primary border shadow-xs" title="Edit User">
                          <Edit2 size={16} />
                        </button>
                        {user.username !== currentUser.username && (
                          <button onClick={() => handleDelete(user.id)} className="btn btn-light btn-sm rounded-circle p-2 text-danger border shadow-xs" title="Delete User">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-5">
                    <div className="text-muted">No users found matching your criteria.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modern Modal */}
      {showModal && (
        <div className="modal show d-block animate-fade-in" style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold fs-4">{editUserId ? 'Update Staff Member' : 'Create New Staff'}</h5>
                <button type="button" className="btn-close shadow-none" onClick={closeAndResetModal}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-muted text-uppercase">Username</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-0"><User size={16} /></span>
                      <input type="text" placeholder="e.g. johndoe" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="form-control bg-light border-0 py-2" required />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-muted text-uppercase">Email Address</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-0"><Mail size={16} /></span>
                      <input type="email" placeholder="email@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="form-control bg-light border-0 py-2" required />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-muted text-uppercase">Password {editUserId && '(Leave blank to keep current)'}</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-0"><Key size={16} /></span>
                      <input type="password" placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="form-control bg-light border-0 py-2" required={!editUserId} />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="form-label small fw-bold text-muted text-uppercase">System Role</label>
                    <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="form-select bg-light border-0 py-2">
                      <option value="PRODUCT_MANAGER">Product Manager</option>
                      <option value="DATA_CURATOR">Data Curator</option>
                      <option value="ADMIN">Administrator</option>
                    </select>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-light w-100 py-2 fw-bold" onClick={closeAndResetModal}>Cancel</button>
                    <button type="submit" className="btn btn-success w-100 py-2 fw-bold shadow-sm">
                      {editUserId ? 'Save Changes' : 'Create Account'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default UserManagement;